import { useCallback, useEffect, useLayoutEffect, useRef, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  ArrowUp,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  Send,
  Sparkles,
} from "lucide-react";
import { HIDDEN_FEED_EVENT_TYPES } from "@quovy/types";
import type { AgentMessage, SignalSentiment } from "@quovy/types";
import { FeedbackButtons } from "../feedback-buttons";
import { IdentityDisplay } from "../identity-display";
import { Badge } from "../../data-display/badge";
import { MarkdownRenderer } from "../../data-display/markdown-renderer";
import { Button } from "../../inputs/button";
import { MarkdownEditor } from "../../inputs/markdown-editor";
import { cn } from "../../utils";
import { MentionTextarea, type Mentionable, getPlainText } from "../../inputs/mention-chip-editor";
import { useLinkComponent, type LinkComponent } from "../../navigation/link-context";
import { TaskStatusBadge } from "./task-status-badge";
import { TaskStatusIcon } from "./task-status-icon";
import { TaskTitleInput } from "./task-title-input";
import { STATUS_LABELS, type TaskUiStatus, formatTaskStatusLabel } from "./task-status";
import { TaskEventType } from "@quovy/types";
import { RelativeTime } from "./relative-time";
import { TaskPropertiesPanel } from "./task-properties-panel";
import type { Tag } from "../../tags/tag-colors";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum visible lines for a comment body before it collapses behind "Show more". */
const MAX_COMMENT_VISIBLE_LINES = 12;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TaskIdentitySummary {
  publicId: string;
  kind: "user" | "agent" | "system" | "unknown";
  name: string;
  email?: string | null;
  avatarUrl: string | null;
}

/** `@` mention catalog from assignable identities. Insert-only — no notify/run. */
export function mentionablesFromIdentities(identities: TaskIdentitySummary[]): Mentionable[] {
  return identities.flatMap((identity) => {
    if (identity.kind !== "user" && identity.kind !== "agent") return [];
    return [
      {
        id: identity.publicId,
        username: identity.name.toLowerCase().replace(/\s+/g, "-"),
        name: identity.name,
        avatarUrl: identity.avatarUrl,
        type: identity.kind,
      },
    ];
  });
}

export interface TaskDetailTask {
  publicId: string;
  taskNumber: number | null;
  title: string | null;
  description: string | null;
  status: TaskUiStatus;
  dueDate: number | null;
  /** Pending deferred agent run (epoch ms). Absent/null once the scheduled run fires. */
  scheduledAt?: number | null;
  createdAt: number;
  updatedAt: number;
  startedAt?: number | null;
  completedAt?: number | null;
  cancelledAt?: number | null;
  primaryResourcePublicId: string | null;
  primaryResourceName?: string | null;
  primaryResourceModelName?: string | null;
  primaryResourceType?: string | null;
  /** Workflow run public ids (`wfr_…`) this task is linked to, if any. */
  workflowRunPublicIds?: string[];
  parentTaskPublicId: string | null;
  createdBy?: TaskIdentitySummary | null;
  owner?: TaskIdentitySummary | null;
  assignee?: TaskIdentitySummary | null;
  assignedBy?: TaskIdentitySummary | null;
  tags?: ReadonlyArray<Tag>;
}

export interface TaskDetailEvent {
  publicId: string;
  eventType: string;
  actor?: TaskIdentitySummary | null;
  data?: Record<string, unknown>;
  createdAt: number;
}

export interface TaskExecutionData {
  status: string;
  agentId?: string | null;
  /** `last_error` / `last_error_code` on the run thread; null while running or on success. */
  error?: { code: string | null; message: string } | null;
  /** The run's step log — agent_messages on the run thread (task runs are threads). */
  messages: AgentMessage[];
}

export interface TaskDetailComment {
  publicId: string;
  resourcePublicId: string;
  content: string;
  version: number;
  createdAt: number;
  updatedAt?: number | null;
  editedAt?: number | null;
  mentionedAgentIds?: string[];
  agentRunPublicId?: string | null;
  executionId?: string | null;
  execution?: TaskExecutionData | null;
  author?: TaskIdentitySummary | null;
  events: TaskDetailEvent[];
}

export interface TaskDetailAttachment {
  publicId: string | null;
  resourcePublicId: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: number;
  updatedAt?: number | null;
  storageKey?: string | null;
  storageProvider?: string | null;
  metadata?: Record<string, unknown> | null;
  addedBy?: TaskIdentitySummary | null;
}

export interface TaskDetailLinkedResource {
  publicId: string;
  resourcePublicId: string;
  linkedPublicId: string;
  resourceType?: string | null;
  resourceName?: string | null;
  resourceModelName?: string | null;
  label?: string | null;
  purpose?: string | null;
  role?: "primary" | "related" | "input" | "output" | "reference" | null;
  createdAt: number;
  createdBy?: TaskIdentitySummary | null;
}

export interface TaskContextSummary {
  publicId: string;
  taskNumber?: number;
  title?: string;
  status: TaskUiStatus;
  agentWorking?: boolean;
  updatedAt: number;
}

export interface TaskDetailData {
  task: TaskDetailTask;
  comments: TaskDetailComment[];
  attachments: TaskDetailAttachment[];
  linkedResources: TaskDetailLinkedResource[];
  events: TaskDetailEvent[];
}

interface TaskDetailViewProps {
  workspaceSlug: string;
  detail: TaskDetailData;
  parentTask?: TaskContextSummary | null;
  subtasks?: TaskContextSummary[];
  /** Aggregate sub-task counts from the detail read (`{ total, completed }`). Falls back to `subtasks.length` when omitted. */
  subtaskCounts?: { total: number; completed: number };
  /** Whether another page of sub-tasks is available (cursor-paginated `next_cursor` is non-null). */
  hasMoreSubtasks?: boolean;
  /** Fetches the next page of sub-tasks. Renders a "Load more" row when provided and `hasMoreSubtasks` is true. */
  onLoadMoreSubtasks?: () => void;
  /** Whether the next page of sub-tasks is currently loading. */
  isLoadingMoreSubtasks?: boolean;
  /** Opens the create-task flow prefilled with this task as the parent. Renders an "Add sub-task" action in the sub-tasks section header. */
  onAddSubtask?: () => void;
  attachmentUrlResolver?: (attachment: TaskDetailAttachment) => string | null;
  /** Open the file preview drawer for a linked file resource (`fil_…`). When
   *  provided, file-type linked resources render as click targets. */
  onFileClick?: (filePublicId: string, filename: string) => void;
  attachmentsContent?: ReactNode;
  /** Optional content rendered directly under the task description. */
  descriptionAddon?: ReactNode;
  commentDraft: string;
  isSubmittingComment?: boolean;
  /** Mentionable entities for the @ popup in the comment input. */
  mentionOptions?: Mentionable[];
  /** Marks mention chips invalid (e.g. an archived agent). */
  getMentionChipState?: (attrs: { id: string }) => { invalid?: boolean } | null | undefined;
  mentionChipStateKey?: unknown;
  /** Banner above the comment box — archived-mention errors, etc. */
  commentNotice?: ReactNode;
  onCommentDraftChange: (value: string) => void;
  onSubmitComment: () => void | Promise<void>;
  onTitleChange?: (value: string) => void | Promise<void>;
  onDescriptionChange?: (value: string) => void | Promise<void>;
  /** Assignable identities for the assignee selector */
  assigneeOptions?: TaskIdentitySummary[];
  /** Callback when assignee is changed */
  onAssigneeChange?: (identity: TaskIdentitySummary | null) => void;
  /** Callback when status is changed */
  onStatusChange?: (status: TaskUiStatus) => void;
  /** Callback when due date is changed — null clears it */
  onDueDateChange?: (date: number | null) => void;
  /** Callback when a deferred agent run is scheduled, changed, or cleared. */
  onScheduledAtChange?: (date: number | null) => void;
  /** Workspace catalog used when task tags are editable. */
  tagCatalog?: ReadonlyArray<Tag>;
  /** Enables task tag editing when provided. */
  onToggleTag?: (tagId: string) => void;
  canCreateTags?: boolean;
  onCreateTag?: (name: string) => void;
  /** Stop a running agent execution. Receives the execution's thread public id. */
  onStopExecution?: (executionId: string) => void;
  /** The execution ID currently being stopped, if any. */
  stoppingExecutionId?: string | null;
  /**
   * Render slot for a comment's agent run log. A task run is a thread; apps/web
   * fills this with the shared chat conversation renderer (ChatMessageList) so
   * packages/ui doesn't carry a second renderer.
   */
  renderRunLog?: (ctx: {
    executionId: string;
    status: string;
    agentId: string | null;
    error: { code: string | null; message: string } | null;
    messages: AgentMessage[];
    onStop?: () => void;
    stopping?: boolean;
  }) => ReactNode;
  /** Callback when a user gives thumbs-up/down feedback on an agent comment. */
  onSignal?: (payload: {
    sentiment: SignalSentiment;
    targetType: string;
    targetId: string;
    contextType: string;
    contextId: string;
    reason?: string | undefined;
  }) => void;
  /** Map of targetId → sentiment for the current user's existing signals. */
  mySignalsByTarget?: Map<string, SignalSentiment>;
  /** Whether the current user is watching this task */
  isWatching?: boolean;
  /** Callback to toggle watch/unwatch */
  onToggleWatch?: () => void;
  /** Whether the watch toggle is in progress */
  isTogglingWatch?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Shorthand for typing CSS custom property style objects. */
function stagger(delayMs: number): CSSProperties {
  return { "--enter-delay": delayMs } as CSSProperties;
}

function getTaskTitle(task: {
  title?: string | null;
  description?: string | null;
  taskNumber?: number | null;
}) {
  return task.title?.trim() || task.description?.trim() || `Task ${task.taskNumber ?? ""}`.trim();
}

const SCHEDULED_SKIP_REASONS: Record<string, string> = {
  no_agent_assignee: "No agent is assigned. Assign an agent and schedule a new run.",
  not_executable: "The task is blocked or closed. Reopen or unblock it and schedule a new run.",
  agent_unavailable:
    "The assigned agent is unavailable. Assign an active agent and schedule a new run.",
  already_running:
    "The assigned agent already has a run in progress. Schedule a new run if another is needed.",
  agent_not_ready:
    "This simulation world cannot run the assigned agent as its source workspace does. Choose another agent for this task.",
};

const EVENT_LABELS: Record<string, string> = {
  [TaskEventType.ScheduledRunSkipped]: "Scheduled run skipped",
  [TaskEventType.ScheduleUpdated]: "Schedule updated",
  [TaskEventType.ScheduledRunStarted]: "Scheduled agent run started",
  [TaskEventType.Created]: "Task created",
  [TaskEventType.Updated]: "Task updated",
  [TaskEventType.Completed]: "Task completed",
  [TaskEventType.Blocked]: "Task blocked",
  [TaskEventType.StatusUpdated]: "Status changed",
  [TaskEventType.CommentAdded]: "Comment added",
  [TaskEventType.AssigneeChanged]: "Assignee changed",
  [TaskEventType.ParentChanged]: "Parent changed",
  [TaskEventType.ParticipantMentioned]: "Participant mentioned",
  [TaskEventType.ParticipantRemoved]: "Participant removed",
  [TaskEventType.ExecutionCreated]: "Run queued",
  [TaskEventType.ExecutionCompleted]: "Run completed",
  [TaskEventType.ExecutionFailed]: "Run failed",
  [TaskEventType.ExecutionCanceled]: "Run canceled",
  [TaskEventType.ExecutionSuperseded]: "Run superseded",
  [TaskEventType.ExecutionActionRecorded]: "Action recorded",
  [TaskEventType.LinkedResourceAdded]: "Resource linked",
  [TaskEventType.LinkedResourceRemoved]: "Resource unlinked",
  // Legacy underscore-format keys (pre-migration data)
  task_created: "Task created",
  task_completed: "Task completed",
  task_cancelled: "Task cancelled",
  status_changed: "Status changed",
  comment_created: "Comment added",
  agent_mentioned: "Agent mentioned",
  run_pending: "Run queued",
  run_started: "Run started",
  run_waiting: "Waiting for input",
  run_completed: "Run completed",
  run_failed: "Run failed",
};

function formatEventLabel(eventType: string) {
  return EVENT_LABELS[eventType] ?? eventType.replaceAll(/[._]/g, " ");
}

const FIELD_LABELS: Record<string, string> = {
  title: "title",
  status: "status",
  priority: "priority",
  description: "description",
  dueDate: "due date",
  labels: "labels",
  outcomeSummary: "outcome summary",
  scheduled_at: "schedule",
  scheduledAt: "schedule",
};

function formatFieldValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === "") return "none";
  if (field === "status" && typeof value === "string" && Object.hasOwn(STATUS_LABELS, value)) {
    return formatTaskStatusLabel(value as TaskUiStatus).toLowerCase();
  }
  if ((field === "scheduled_at" || field === "scheduledAt") && typeof value === "string") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return scheduleDateTimeFormat.format(date);
  }
  if (field === "dueDate" && typeof value === "string") {
    return new Date(value).toLocaleDateString();
  }
  if (field === "labels" && Array.isArray(value)) {
    return value.length === 0 ? "none" : value.join(", ");
  }
  return String(value);
}

type FieldChange = {
  field: string;
  label: string;
  from?: string;
  to?: string;
  /** Raw status keys, kept because `from`/`to` hold the humanised label ("to do", not "todo"). */
  fromStatus?: TaskUiStatus;
  toStatus?: TaskUiStatus;
};

function statusKeys(from: unknown, to: unknown): Pick<FieldChange, "fromStatus" | "toStatus"> {
  const keys: Pick<FieldChange, "fromStatus" | "toStatus"> = {};
  if (typeof from === "string" && Object.hasOwn(STATUS_LABELS, from))
    keys.fromStatus = from as TaskUiStatus;
  if (typeof to === "string" && Object.hasOwn(STATUS_LABELS, to))
    keys.toStatus = to as TaskUiStatus;
  return keys;
}

/** Build structured change entries for a task.updated event. */
function getUpdatedEventChanges(data: Record<string, unknown> | undefined): FieldChange[] | null {
  const changes = data?.changes as Record<string, { from: unknown; to: unknown }> | undefined;
  if (!changes || Object.keys(changes).length === 0) return null;

  const parts: FieldChange[] = [];
  for (const [field, { from, to }] of Object.entries(changes)) {
    const label = FIELD_LABELS[field] ?? field;
    if (field === "description" || field === "outcomeSummary" || field === "acceptanceCriteria") {
      parts.push({ field, label });
    } else {
      parts.push({
        field,
        label,
        from: formatFieldValue(field, from),
        to: formatFieldValue(field, to),
        ...(field === "status" ? statusKeys(from, to) : {}),
      });
    }
  }
  return parts;
}

// ---------------------------------------------------------------------------
// Activity feed collapsing
// ---------------------------------------------------------------------------

/** Identical events from one actor fold into one row while they all fall within this window of the first. */
const EVENT_COLLAPSE_WINDOW_MS = 5 * 60 * 1000;

/** Events whose row text carries per-event detail; folding them would hide it. */
const NON_COLLAPSIBLE_EVENT_TYPES: ReadonlySet<string> = new Set([
  TaskEventType.Created,
  TaskEventType.Updated,
  TaskEventType.ScheduleUpdated,
  TaskEventType.ScheduledRunStarted,
  TaskEventType.ScheduledRunSkipped,
  TaskEventType.Blocked,
  TaskEventType.Completed,
  TaskEventType.ExecutionCanceled,
  TaskEventType.ExecutionFailed,
]);

const DISTINGUISHING_DATA_KEYS = ["status", "changes", "reason", "error", "outcomeSummary"];

/** Null when the event must stay its own row; otherwise the key two events must share to fold. */
function getEventCollapseKey(evt: TaskDetailEvent): string | null {
  if (NON_COLLAPSIBLE_EVENT_TYPES.has(evt.eventType)) return null;
  if (
    DISTINGUISHING_DATA_KEYS.some(
      (key) => evt.data?.[key] !== undefined && evt.data?.[key] !== null,
    )
  ) {
    return null;
  }
  const actorKey = evt.actor?.publicId ?? evt.actor?.email ?? evt.actor?.name ?? "system";
  return `${actorKey}|${evt.eventType}`;
}

const EVENT_GROUP_LABELS: Record<string, (count: number) => string> = {
  [TaskEventType.LinkedResourceAdded]: (n) => `${n} resources linked`,
  [TaskEventType.LinkedResourceRemoved]: (n) => `${n} resources unlinked`,
  [TaskEventType.ExecutionActionRecorded]: (n) => `${n} actions recorded`,
  [TaskEventType.ParticipantRemoved]: (n) => `${n} participants removed`,
};

function formatEventGroupLabel(eventType: string, count: number) {
  return EVENT_GROUP_LABELS[eventType]?.(count) ?? `${formatEventLabel(eventType)} ×${count}`;
}

/** Actor avatar + name, shared by single event rows and event-group header rows. */
function ActorIdentity({
  actor,
  workspaceSlug,
}: {
  actor: TaskIdentitySummary | null | undefined;
  workspaceSlug: string;
}) {
  return (
    <IdentityDisplay
      kind={actor?.kind ?? "system"}
      name={actor?.name ?? "System"}
      email={actor?.email}
      publicId={actor?.publicId}
      avatarUrl={actor?.avatarUrl}
      workspaceSlug={workspaceSlug}
      variant="card"
      className="shrink-0 font-medium"
    />
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const scheduleDateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function ScheduleDateTime({ value }: { value: string }) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return <time dateTime={date.toISOString()}>{scheduleDateTimeFormat.format(date)}</time>;
}

function ScheduleUpdatedActivity({ data }: { data: Record<string, unknown> | undefined }) {
  const previous =
    typeof data?.previous_scheduled_at === "string" ? data.previous_scheduled_at : null;
  const next = typeof data?.scheduled_at === "string" ? data.scheduled_at : null;

  if (previous && next) {
    return (
      <>
        rescheduled the agent run from <ScheduleDateTime value={previous} /> to{" "}
        <ScheduleDateTime value={next} />
      </>
    );
  }
  if (next) {
    return (
      <>
        scheduled the agent run for <ScheduleDateTime value={next} />
      </>
    );
  }
  return <>cleared the scheduled agent run</>;
}

/** Router-aware in-app link with plain `<a>` fallback when no LinkProvider is set. */
function TaskHierarchyLink({
  link: Link,
  href,
  className,
  children,
}: {
  link: LinkComponent | null;
  href: string;
  className: string;
  children: ReactNode;
}) {
  if (Link) {
    return (
      <Link to={href} className={className}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

function InlineDescription({
  value,
  placeholder,
  onChange,
  mentionOptions,
}: {
  value: string;
  placeholder: string;
  onChange?: ((value: string) => void | Promise<void>) | undefined;
  mentionOptions?: Mentionable[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [focused, setFocused] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const dirtyRef = useRef(false);

  useLayoutEffect(() => {
    setExpanded(false);
    dirtyRef.current = false;
  }, [value]);

  // TipTap mounts after the first paint (`immediatelyRender: false`). Observe
  // the surface so the 240px clamp and "Show more" run once the document is
  // actually in the DOM, not against an empty editor. Measure the inner
  // document, not this wrapper — toggling maxHeight on the observed node
  // retriggers ResizeObserver and can loop.
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const measure = () => {
      const inner = el.querySelector(".ProseMirror") ?? el;
      const overflows = inner.scrollHeight > 240;
      setIsOverflowing((current) => (current === overflows ? current : overflows));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [value, focused, expanded]);

  return (
    <div className="mt-2">
      <div
        ref={contentRef}
        style={
          !expanded && !focused && isOverflowing
            ? { maxHeight: "240px", overflowY: "hidden" }
            : undefined
        }
        className={cn(
          "rounded-md px-2 py-1.5 text-sm leading-relaxed text-foreground",
          onChange && "cursor-text",
        )}
      >
        <MarkdownEditor
          variant="seamless"
          value={value}
          placeholder={placeholder}
          editable={!!onChange}
          aria-label="Task description"
          {...(mentionOptions ? { mentionOptions } : {})}
          onChange={() => {
            dirtyRef.current = true;
          }}
          onFocus={() => {
            setFocused(true);
            setExpanded(true);
          }}
          onBlur={(next) => {
            setFocused(false);
            if (!dirtyRef.current) return;
            dirtyRef.current = false;
            const trimmed = next.trim();
            if (trimmed !== value.trim() && onChange) void onChange(trimmed);
          }}
        />
      </div>
      {isOverflowing && !focused ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          className={cn(
            "mt-1 h-auto gap-1 px-2 py-1 text-xs text-muted-foreground/70 hover:text-muted-foreground",
            expanded &&
              "sticky bottom-4 z-10 self-start rounded-md bg-background/90 backdrop-blur-sm",
          )}
        >
          {expanded ? (
            <>
              <ChevronUp className="h-3 w-3" />
              Collapse
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              Show more
            </>
          )}
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rich comment body
// ---------------------------------------------------------------------------

function CommentBody({ content }: { content: string }) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Compute a pixel max-height from the line count. We use the container's
  // computed line-height so it stays in sync with whatever prose styles apply.
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    // Temporarily remove any height constraint so we can measure the full content.
    const prev = el.style.maxHeight;
    el.style.maxHeight = "none";
    const fullHeight = el.scrollHeight;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const maxH = Math.ceil(lineHeight * MAX_COMMENT_VISIBLE_LINES);
    el.style.maxHeight = prev;
    setCollapsedHeight(maxH);
    setIsOverflowing(fullHeight > maxH + 2);
  }, [content]);

  const inner = (
    <MarkdownRenderer content={content} density="compact" className="text-sm leading-6" />
  );

  const shouldClamp = !isExpanded && isOverflowing && collapsedHeight !== null;

  return (
    <div>
      <div
        ref={bodyRef}
        className={shouldClamp ? "overflow-hidden relative" : undefined}
        style={shouldClamp ? { maxHeight: collapsedHeight } : undefined}
      >
        {inner}
        {shouldClamp && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-card to-transparent" />
        )}
      </div>
      {isOverflowing && (
        <Button
          type="button"
          variant="link"
          onClick={() => setIsExpanded((v) => !v)}
          className="mt-1.5 h-auto p-0 text-xs font-medium text-muted-foreground hover:text-foreground hover:no-underline"
        >
          {isExpanded ? (
            <>
              Show less <ChevronUp className="h-3 w-3" />
            </>
          ) : (
            <>
              Show more <ChevronDown className="h-3 w-3" />
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// InlineTitle — always the same field. Focusing it adds a caret, not a border.
// ---------------------------------------------------------------------------

function InlineTitle({
  value,
  onChange,
}: {
  value: string;
  onChange?: ((value: string) => void | Promise<void>) | undefined;
}) {
  const [draft, setDraft] = useState(value);
  const focusedRef = useRef(false);
  const cancelingRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(value);
  }, [value]);

  const commit = useCallback(() => {
    focusedRef.current = false;
    if (cancelingRef.current) {
      cancelingRef.current = false;
      setDraft(value);
      return;
    }
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value.trim() && onChange) {
      void onChange(trimmed);
      return;
    }
    setDraft(value);
  }, [draft, value, onChange]);

  return (
    <TaskTitleInput
      value={draft}
      {...(onChange ? { onChange: setDraft } : {})}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === "Escape") {
          // blur() fires commit synchronously, before setDraft(value) lands.
          cancelingRef.current = true;
          setDraft(value);
          e.currentTarget.blur();
        }
      }}
      className="flex-1 truncate"
    />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function TaskDetailView({
  workspaceSlug,
  detail,
  parentTask = null,
  subtasks = [],
  subtaskCounts,
  hasMoreSubtasks = false,
  onLoadMoreSubtasks,
  isLoadingMoreSubtasks = false,
  onAddSubtask,
  attachmentUrlResolver,
  onFileClick,
  attachmentsContent,
  descriptionAddon,
  commentDraft,
  isSubmittingComment = false,
  mentionOptions,
  getMentionChipState,
  mentionChipStateKey,
  commentNotice,
  onCommentDraftChange,
  onSubmitComment,
  onTitleChange,
  onDescriptionChange,
  assigneeOptions,
  onAssigneeChange,
  onStatusChange,
  onDueDateChange,
  onScheduledAtChange,
  tagCatalog = [],
  onToggleTag,
  canCreateTags = false,
  onCreateTag,
  onStopExecution,
  stoppingExecutionId,
  renderRunLog,
  onSignal,
  mySignalsByTarget,
  isWatching,
  onToggleWatch,
  isTogglingWatch,
}: TaskDetailViewProps) {
  const LinkComponent = useLinkComponent();
  const { task, comments, attachments, linkedResources, events } = detail;

  // Merge comments and events into a single chronological timeline
  type TimelineItem =
    | { kind: "comment"; createdAt: number; comment: TaskDetailComment }
    | { kind: "event"; createdAt: number; event: TaskDetailEvent }
    | { kind: "event-group"; createdAt: number; events: TaskDetailEvent[] };

  // Collect event IDs that are already embedded inside comments so we don't duplicate them
  const commentEventIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of comments) {
      for (const e of c.events) ids.add(e.publicId);
    }
    return ids;
  }, [comments]);

  const timeline = useMemo(() => {
    const items: TimelineItem[] = [
      ...comments.map((c) => ({ kind: "comment" as const, createdAt: c.createdAt, comment: c })),
      ...events
        .filter(
          (e) => !commentEventIds.has(e.publicId) && !HIDDEN_FEED_EVENT_TYPES.has(e.eventType),
        )
        .map((e) => ({ kind: "event" as const, createdAt: e.createdAt, event: e })),
    ];
    items.sort((a, b) => a.createdAt - b.createdAt);

    // Fold runs of identical events (same actor, same type, back to back within
    // the window) into one group row so ten "Resource linked" lines read as one.
    const folded: TimelineItem[] = [];
    let run: { key: string; events: TaskDetailEvent[] } | null = null;
    const flushRun = () => {
      if (!run) return;
      folded.push(
        run.events.length > 1
          ? { kind: "event-group", createdAt: run.events[0]!.createdAt, events: run.events }
          : { kind: "event", createdAt: run.events[0]!.createdAt, event: run.events[0]! },
      );
      run = null;
    };
    for (const item of items) {
      const key = item.kind === "event" ? getEventCollapseKey(item.event) : null;
      if (item.kind !== "event" || key === null) {
        flushRun();
        folded.push(item);
        continue;
      }
      const start = run?.events[0];
      if (
        run &&
        start &&
        run.key === key &&
        item.createdAt - start.createdAt <= EVENT_COLLAPSE_WINDOW_MS
      ) {
        run.events.push(item.event);
      } else {
        flushRun();
        run = { key, events: [item.event] };
      }
    }
    flushRun();
    return folded;
  }, [comments, events, commentEventIds]);

  const [expandedEventGroups, setExpandedEventGroups] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const toggleEventGroup = useCallback((groupId: string) => {
    setExpandedEventGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 200);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll to and highlight a hash-targeted comment.
  // Track the last hash we scrolled to so we only auto-scroll once per anchor
  // (timeline reference changes on every parent re-render and must not re-trigger).
  const scrolledHashRef = useRef<string | null>(null);

  const scrollToComment = useCallback((force?: boolean) => {
    const hash = window.location.hash;
    if (!hash.startsWith("#comment-")) return;
    if (!force && hash === scrolledHashRef.current) return;
    const el = document.getElementById(hash.slice(1));
    if (!el) return;
    scrolledHashRef.current = hash;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-primary/50");
    setTimeout(() => {
      el.classList.remove("ring-2", "ring-primary/50");
    }, 2000);
  }, []);

  // Trigger on initial load / timeline change (only scrolls once per hash)
  useEffect(() => {
    const raf = requestAnimationFrame(() => scrollToComment());
    return () => cancelAnimationFrame(raf);
  }, [timeline, scrollToComment]);

  // Trigger when clicking a comment permalink while already on the page
  useEffect(() => {
    const handler = () => scrollToComment(true);
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, [scrollToComment]);

  const renderEventRow = (evt: TaskDetailEvent, index: number, nested = false) => (
    <div
      key={evt.publicId}
      className={cn("animate-entrance flex items-center gap-2.5 py-2", nested ? "pl-10" : "pl-4")}
      // Nested rows mount on click, so they must not inherit the page-load stagger offset.
      style={stagger(nested ? index * 30 : 120 + index * 40)}
    >
      <ActorIdentity actor={evt.actor} workspaceSlug={workspaceSlug} />
      <span className="min-w-0 text-[13px] text-muted-foreground">
        {evt.eventType === TaskEventType.ScheduleUpdated ? (
          <ScheduleUpdatedActivity data={evt.data} />
        ) : evt.eventType === TaskEventType.ScheduledRunStarted ? (
          <>
            responded to the schedule set for{" "}
            {typeof evt.data?.scheduled_at === "string" ? (
              <ScheduleDateTime value={evt.data.scheduled_at} />
            ) : (
              "the selected time"
            )}
          </>
        ) : evt.eventType === TaskEventType.Updated &&
          getUpdatedEventChanges(evt.data as Record<string, unknown>) ? (
          <span>
            {getUpdatedEventChanges(evt.data as Record<string, unknown>)!.map((change, i) => (
              <span key={i}>
                {i > 0 && ", "}
                {change.from !== undefined ? (
                  <>
                    changed {change.label} from{" "}
                    {change.fromStatus ? (
                      <TaskStatusBadge status={change.fromStatus} />
                    ) : (
                      <code className="rounded bg-muted px-1 py-0.5 text-foreground">
                        {change.from}
                      </code>
                    )}{" "}
                    to{" "}
                    {change.toStatus ? (
                      <TaskStatusBadge status={change.toStatus} />
                    ) : (
                      <code className="rounded bg-muted px-1 py-0.5 text-foreground">
                        {change.to}
                      </code>
                    )}
                  </>
                ) : (
                  <>updated {change.label}</>
                )}
              </span>
            ))}
          </span>
        ) : (
          <>
            {formatEventLabel(evt.eventType)}
            {typeof evt.data?.status === "string" &&
            Object.hasOwn(STATUS_LABELS, evt.data.status) ? (
              evt.eventType === TaskEventType.Created ? (
                <span>
                  {" "}
                  with status <TaskStatusBadge status={evt.data.status as TaskUiStatus} />
                </span>
              ) : (
                <span>
                  {" "}
                  to <TaskStatusBadge status={evt.data.status as TaskUiStatus} />
                </span>
              )
            ) : null}
          </>
        )}
        {evt.eventType === TaskEventType.ScheduledRunSkipped ? (
          <span>
            {typeof evt.data?.scheduled_at === "string" ? (
              <>
                {" "}
                · <RelativeTime date={new Date(evt.data.scheduled_at)} />
              </>
            ) : null}
            {" — "}
            {(typeof evt.data?.detail === "string" && evt.data.detail) ||
              (SCHEDULED_SKIP_REASONS[String(evt.data?.reason)] ??
                "The scheduled run could not start. Review the task and schedule a new run.")}
          </span>
        ) : null}
        {/* Blocked reason */}
        {evt.eventType === "task.blocked" &&
        typeof evt.data?.reason === "string" &&
        evt.data.reason ? (
          <span>
            {" — "}
            {String(evt.data.reason)}
          </span>
        ) : null}
        {/* Completed outcome */}
        {evt.eventType === "task.completed" &&
        typeof evt.data?.outcomeSummary === "string" &&
        evt.data.outcomeSummary ? (
          <span>
            {" — "}
            {String(evt.data.outcomeSummary)}
          </span>
        ) : null}
        {/* Execution canceled reason */}
        {evt.eventType === "task.execution_canceled" &&
        typeof evt.data?.reason === "string" &&
        evt.data.reason ? (
          <span>
            {" — "}
            {String(evt.data.reason)}
          </span>
        ) : null}
        {/* Execution failure error */}
        {evt.eventType === "task.execution_failed" &&
        typeof evt.data?.error === "string" &&
        evt.data.error ? (
          <span>
            {" — "}
            {String(evt.data.error)}
          </span>
        ) : null}
        {typeof evt.data?.agentPublicId === "string" ? (
          <span> · {String(evt.data.agentPublicId)}</span>
        ) : null}
        <span className="ml-1.5 text-muted-foreground/60">
          {" · "}
          <RelativeTime date={new Date(evt.createdAt)} />
        </span>
      </span>
    </div>
  );

  return (
    // The two-column split is keyed on THIS component's own width, not the
    // viewport: the same view renders full-width on `/tasks/:id` and inline
    // inside the email reading pane, where the viewport can be wide while the
    // container is narrow. `45rem` is the container width `/tasks/:id` has at
    // the old `lg` (64rem) viewport breakpoint — 64rem minus the 16rem sidebar
    // and the shell's 3rem of page padding — so that page switches at exactly
    // the same point it does today.
    <div className="@container/task-detail w-full">
      <div className="grid gap-6 @[45rem]/task-detail:grid-cols-[minmax(0,1fr)_280px]">
        {/* Left column — header + activity */}
        <div className="min-w-0">
          {/* Header */}
          <div className="animate-entrance" style={stagger(0)}>
            <div className="flex items-center gap-2.5">
              <Badge
                variant="outline"
                className="shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold"
              >
                TASK-{task.taskNumber ?? "?"}
              </Badge>
              <InlineTitle value={getTaskTitle(task)} onChange={onTitleChange} />
              {onToggleWatch && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onToggleWatch}
                  disabled={isTogglingWatch}
                  className="ml-auto shrink-0 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {isWatching ? (
                    <>
                      <EyeOff className="size-3.5" />
                      <span>Unwatch</span>
                    </>
                  ) : (
                    <>
                      <Eye className="size-3.5" />
                      <span>Watch</span>
                    </>
                  )}
                </Button>
              )}
            </div>
            {/* Parent task breadcrumb — sits below the title so task titles are at the
                same height whether or not the task has a parent. */}
            {parentTask && (
              <div className="mt-1.5">
                <TaskHierarchyLink
                  link={LinkComponent}
                  href={`/w/${workspaceSlug}/tasks/${parentTask.publicId}`}
                  className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
                >
                  <span className="text-[11px] font-medium">
                    TASK-{parentTask.taskNumber ?? "?"}
                  </span>
                  <span>{getTaskTitle(parentTask)}</span>
                  <ArrowUpRight className="h-3 w-3" />
                </TaskHierarchyLink>
              </div>
            )}
            <InlineDescription
              value={task.description ?? ""}
              placeholder="Add a description… Type @ to mention"
              onChange={onDescriptionChange}
              {...(assigneeOptions && assigneeOptions.length > 0
                ? { mentionOptions: mentionablesFromIdentities(assigneeOptions) }
                : mentionOptions
                  ? { mentionOptions }
                  : {})}
            />
            {descriptionAddon ? <div className="mt-4">{descriptionAddon}</div> : null}
          </div>

          {/* Subtasks table — collapsible, open by default */}
          {(subtasks.length > 0 || onAddSubtask) && (
            <details className="animate-entrance mt-5 group" open style={stagger(60)}>
              <summary className="mb-2 flex cursor-pointer list-none items-center gap-1 text-xs font-medium text-muted-foreground select-none [&::-webkit-details-marker]:hidden">
                <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-0 -rotate-90" />
                Sub-issues
                <span className="ml-1 text-muted-foreground/50">
                  {subtaskCounts?.total ?? subtasks.length}
                </span>
                {onAddSubtask && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAddSubtask();
                    }}
                    className="ml-auto h-6 gap-1 px-2 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Plus className="h-3 w-3" />
                    Add sub-task
                  </Button>
                )}
              </summary>
              {subtasks.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-border">
                  {subtasks.map((subtask, i) => (
                    <TaskHierarchyLink
                      key={subtask.publicId}
                      link={LinkComponent}
                      href={`/w/${workspaceSlug}/tasks/${subtask.publicId}`}
                      className={`flex items-center gap-3 px-3 py-2 text-[13px] transition-colors hover:bg-muted/50 ${
                        i > 0 ? "border-t border-border" : ""
                      }`}
                    >
                      <TaskStatusIcon
                        status={subtask.status}
                        working={subtask.agentWorking ?? false}
                      />
                      <span className="min-w-0 flex-1 truncate text-foreground">
                        {getTaskTitle(subtask)}
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        TASK-{subtask.taskNumber ?? "?"}
                      </span>
                    </TaskHierarchyLink>
                  ))}
                  {hasMoreSubtasks && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={onLoadMoreSubtasks}
                      disabled={isLoadingMoreSubtasks}
                      className="w-full justify-center gap-1.5 rounded-none border-t border-border text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      {isLoadingMoreSubtasks ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                      {isLoadingMoreSubtasks ? "Loading…" : "Load more"}
                    </Button>
                  )}
                </div>
              )}
            </details>
          )}

          {/* Activity timeline */}
          <div className="animate-entrance mt-6" style={stagger(80)}>
            <h2 className="mb-4 text-sm font-semibold text-foreground">Activity</h2>

            {/* Timeline items */}
            <div className="space-y-0">
              {timeline.map((item, index) => {
                if (item.kind === "event") return renderEventRow(item.event, index);
                if (item.kind === "event-group") {
                  const first = item.events[0]!;
                  const last = item.events.at(-1)!;
                  const groupId = first.publicId;
                  const expanded = expandedEventGroups.has(groupId);
                  return (
                    <div key={groupId}>
                      <div
                        className="animate-entrance flex items-center gap-2.5 py-2 pl-4"
                        style={stagger(120 + index * 40)}
                      >
                        <ActorIdentity actor={first.actor} workspaceSlug={workspaceSlug} />
                        <span className="min-w-0 text-[13px] text-muted-foreground">
                          {formatEventGroupLabel(first.eventType, item.events.length)}
                          <span className="ml-1.5 text-muted-foreground/60">
                            {" · "}
                            <RelativeTime date={new Date(last.createdAt)} />
                          </span>
                        </span>
                        <Button
                          type="button"
                          variant="link"
                          size="xs"
                          className="h-auto px-1 text-xs text-muted-foreground"
                          aria-expanded={expanded}
                          onClick={() => toggleEventGroup(groupId)}
                        >
                          {expanded ? "Hide" : "Show all"}
                        </Button>
                      </div>
                      {expanded ? item.events.map((evt, i) => renderEventRow(evt, i, true)) : null}
                    </div>
                  );
                }

                const comment = item.comment;
                return (
                  <div
                    key={comment.publicId}
                    className="animate-entrance py-3"
                    style={stagger(120 + index * 40)}
                  >
                    <div
                      id={`comment-${comment.publicId}`}
                      className="scroll-mt-24 rounded-lg border border-border bg-card transition-shadow duration-500"
                    >
                      <div className="flex items-center gap-2.5 border-b border-border/70 px-4 py-2.5">
                        <span className="flex min-w-0 flex-1 items-center gap-1.5">
                          <IdentityDisplay
                            kind={comment.author?.kind ?? "unknown"}
                            name={comment.author?.name ?? "Unknown"}
                            email={comment.author?.email}
                            publicId={comment.author?.publicId}
                            avatarUrl={comment.author?.avatarUrl}
                            workspaceSlug={workspaceSlug}
                            variant="card"
                            className="min-w-0 font-medium"
                          />
                          <span className="shrink-0 text-[13px] text-muted-foreground/60">
                            <RelativeTime
                              date={new Date(comment.createdAt)}
                              href={`#comment-${comment.publicId}`}
                            />
                            {comment.editedAt ? " · edited" : ""}
                          </span>
                        </span>
                        {comment.mentionedAgentIds?.length ? (
                          <Badge
                            variant="outline"
                            className="shrink-0 rounded-full px-2 py-0 text-[10px]"
                          >
                            <Sparkles className="mr-1 h-3 w-3" />
                            {comment.mentionedAgentIds.length} mention
                            {comment.mentionedAgentIds.length > 1 ? "s" : ""}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="px-4 py-3 text-sm leading-relaxed text-foreground">
                        <CommentBody content={comment.content} />
                      </div>
                      {comment.executionId && comment.execution && renderRunLog ? (
                        <div className="border-t border-border/50">
                          {renderRunLog({
                            executionId: comment.executionId,
                            status: comment.execution.status,
                            agentId: comment.execution.agentId ?? null,
                            error: comment.execution.error ?? null,
                            messages: comment.execution.messages,
                            ...(onStopExecution
                              ? {
                                  onStop: () => onStopExecution(comment.executionId!),
                                  stopping: stoppingExecutionId === comment.executionId,
                                }
                              : {}),
                          })}
                        </div>
                      ) : null}
                      {comment.author?.kind === "agent" && onSignal ? (
                        <div className="border-t border-border/50 px-4 py-1.5">
                          <FeedbackButtons
                            sentiment={mySignalsByTarget?.get(comment.publicId) ?? null}
                            onSubmit={(sentiment, reason) =>
                              onSignal({
                                sentiment,
                                targetType: "task_comment",
                                targetId: comment.publicId,
                                contextType: "task",
                                contextId: task.publicId,
                                reason,
                              })
                            }
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}

              {timeline.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">No activity yet.</p>
              )}
            </div>

            {/* Comment input — positioned at the bottom of the activity stream like Linear */}
            <div className="mt-4 space-y-3">
              {commentNotice}
              <div className="min-w-0 rounded-lg border border-border bg-card transition-[border-color,box-shadow] duration-200 focus-within:border-border focus-within:shadow-[0_0_0_1px_var(--border)]">
                <MentionTextarea
                  value={commentDraft}
                  onChange={onCommentDraftChange}
                  placeholder="Leave a comment… Type @ to mention"
                  mentionOptions={mentionOptions ?? []}
                  rows={3}
                  disabled={isSubmittingComment}
                  onSubmit={() => void onSubmitComment()}
                  {...(getMentionChipState ? { getChipState: getMentionChipState } : {})}
                  {...(mentionChipStateKey !== undefined
                    ? { chipStateKey: mentionChipStateKey }
                    : {})}
                  className="!space-y-0 [&_.mention-chip-editor]:min-h-[72px] [&_.mention-chip-editor]:border-0 [&_.mention-chip-editor]:bg-transparent [&_.mention-chip-editor]:shadow-none [&_.mention-chip-editor]:ring-0 [&_.mention-chip-editor]:focus-within:ring-0 [&_.mention-chip-editor]:focus-within:border-transparent"
                />
                <div className="flex items-center justify-between gap-3 border-t border-border/70 px-3 py-2">
                  <span className="text-xs text-muted-foreground/60">
                    <code className="rounded bg-muted px-1 py-0.5 text-[10px]">@</code> to mention ·{" "}
                    <code className="rounded bg-muted px-1 py-0.5 text-[10px]">⌘ Enter</code> to
                    submit
                  </span>
                  <Button
                    size="sm"
                    onClick={() => void onSubmitComment()}
                    disabled={isSubmittingComment || !getPlainText(commentDraft).trim()}
                    className="h-7 px-3 text-xs"
                  >
                    {isSubmittingComment ? (
                      <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                    ) : (
                      <Send className="mr-1.5 h-3 w-3" />
                    )}
                    {isSubmittingComment ? "Posting…" : "Comment"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar — sticky so it stays visible while scrolling the timeline */}
        <aside className="animate-entrance" style={stagger(60)}>
          <div className="sticky top-[calc(1.5rem+var(--app-banner-offset))] space-y-0">
            {showScrollTop && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="mb-3 w-full bg-card text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <ArrowUp className="h-3 w-3" />
                Scroll to top
              </Button>
            )}
            <TaskPropertiesPanel
              workspaceSlug={workspaceSlug}
              task={task}
              attachments={attachments}
              linkedResources={linkedResources}
              tagCatalog={tagCatalog}
              canCreateTags={canCreateTags}
              {...(attachmentsContent !== undefined ? { attachmentsContent } : {})}
              {...(attachmentUrlResolver ? { attachmentUrlResolver } : {})}
              {...(onFileClick ? { onFileClick } : {})}
              {...(assigneeOptions ? { assigneeOptions } : {})}
              {...(onAssigneeChange ? { onAssigneeChange } : {})}
              {...(onStatusChange ? { onStatusChange } : {})}
              {...(onDueDateChange ? { onDueDateChange } : {})}
              {...(onScheduledAtChange ? { onScheduledAtChange } : {})}
              {...(onToggleTag ? { onToggleTag } : {})}
              {...(onCreateTag ? { onCreateTag } : {})}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
